import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Switch from "@mui/material/Switch";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";

import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { isValidEmail } from "../../utils/validation";

export default function Pricing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isAnnual, setIsAnnual] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [email, setEmail] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [emailError, setEmailError] = useState("");

  const tiers = [
    {
      id: "free",
      title: t("pricing.silver.title"),
      subheader: t("pricing.silver.subheader"),
      price: t("pricing.silver.price"),
      annualPrice: t("pricing.silver.annualPrice"),
      description: t("pricing.silver.description", { returnObjects: true }),
      excludedFeature: t("pricing.silver.excludedFeature", { returnObjects: true }),
      buttonText: t("pricing.silver.buttonText"),
      buttonVariant: "outlined",
      buttonColor: "primary",
      action: "signup",
    },
    {
      id: "pro",
      title: t("pricing.gold.title"),
      subheader: t("pricing.gold.subheader"),
      price: t("pricing.gold.price"),
      annualPrice: t("pricing.gold.annualPrice"),
      description: t("pricing.gold.description", { returnObjects: true }),
      excludedFeature: t("pricing.gold.excludedFeature", { returnObjects: true }),
      buttonText: t("pricing.gold.buttonText"),
      buttonVariant: "contained",
      buttonColor: "secondary",
      ribbonColor: "warning.main",
      ribbonText: t("pricing.gold.ribbonText"),
      highlighted: true,
      action: "signup",
    },
    {
      id: "custom",
      title: t("pricing.custom.title"),
      price: t("pricing.custom.price") || "",
      annualPrice: t("pricing.custom.annualPrice") || "",
      subheader: t("pricing.custom.subheader"),
      description: t("pricing.custom.description", { returnObjects: true }),
      excludedFeature: t("pricing.custom.excludedFeature", { returnObjects: true }),
      buttonText: t("pricing.custom.buttonText"),
      buttonVariant: "contained",
      buttonColor: "secondary",
    },
  ];

  const handleSubscribe = async () => {
    setEmailError("");

    if (!email) {
      setEmailError(t("pricing.errors.emailRequired"));
      return;
    }

    if (!isValidEmail(email)) {
      setEmailError(t("pricing.errors.emailInvalid"));
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/infos/subscribe`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ 
          email,
          plan: selectedPlan 
        }),
      });

      if (response.ok) {
        setSnackbarMessage(t("pricing.success.subscription"));
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setEmail("");
        setOpenDialog(false);
      } else {
        throw new Error("Failed to subscribe");
      }
    } catch (error) {
      console.error("Erreur lors de l'inscription :", error);
      setSnackbarMessage(t("pricing.errors.subscriptionFailed"));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handlePlanClick = (planTitle) => {
    const tier = tiers.find((item) => item.title === planTitle);
    if (tier?.action === "signup") {
      navigate("/SignUpPage");
      return;
    }

    setSelectedPlan(planTitle);
    setOpenDialog(true);
  };

  return (  
    <section id="pricing">
      <Container
        sx={{
          pt: { xs: 4, sm: 8 },
          pb: { xs: 8, sm: 8 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          // gap: { xs: 3, sm: 6 },
        }}
      >
        <Box
          sx={{
            width: { sm: "100%", md: "60%" },
            textAlign: { sm: "left", md: "center" },
          }}
        >
          <Typography component="h2" variant="h4" gutterBottom>
            {t("pricing.title")}
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            {t("pricing.subtitle")}
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            mt: 3,
            gap: 2 
          }}>
            <Typography 
              variant="body1" 
              sx={{ 
                color: !isAnnual ? 'primary.main' : 'text.secondary',
                fontWeight: !isAnnual ? 'bold' : 'normal'
              }}
            >
              {t("pricing.monthly")}
            </Typography>
            
            <Switch
              checked={isAnnual}
              onChange={(e) => setIsAnnual(e.target.checked)}
              color="primary"
            />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: isAnnual ? 'primary.main' : 'text.secondary',
                  fontWeight: isAnnual ? 'bold' : 'normal'
                }}
              >
                {t("pricing.annually")}
              </Typography>
              {isAnnual && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'success.main',
                    fontWeight: 'bold'
                  }}
                >
                  ({t("pricing.savePercent")})
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
        <Grid container spacing={3} sx={{ justifyContent: "center" }}>
          {tiers.map((tier) => (
            <Grid item xs={12} sm={6} md={4} key={tier.id}>
              <Card
                sx={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  p: 2,
                  gap: 4,
                  border: tier.highlighted ? "none" : "1px solid",
                  boxShadow: tier.highlighted ? "0 8px 12px rgba(0, 0, 0, 0.2)" : "none",
                  background: tier.highlighted ? "radial-gradient(circle at 50% 0%, #2e3b55, #161e2a)" : "inherit",
                }}
              >
                {/* Ribbon */}
                {/* {tier.ribbonText && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 16,
                      ...(i18n.language === 'he' ? {
                        left: -40,
                        transform: "rotate(-45deg)",
                      } : {
                        right: -40,
                        transform: "rotate(45deg)",
                      }),
                      backgroundColor: tier.ribbonColor,
                      color: "white",
                      padding: "4px 40px",
                      zIndex: 1,
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                      textAlign: "center",
                    }}
                  >
                    {tier.ribbonText}
                  </Box>
                )} */}

                {/* Card Content */}
                <CardContent>
                  <Box
                    sx={[
                      {
                        mb: 1,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 2,
                      },
                      tier.highlighted
                        ? { color: "grey.100" }
                        : { color: "" },
                    ]}
                  >
                    <Typography component="h3" variant="h6">
                      {tier.title}
                    </Typography>
                    {tier.highlighted && (
                      <Chip icon={<AutoAwesomeIcon />} label={tier.subheader} />
                    )}
                  </Box>
                  <Box
                    sx={[
                      {
                        display: "flex",
                        alignItems: "baseline",
                      },
                      tier.highlighted
                        ? { color: "grey.50" }
                        : { color: null },
                    ]}
                  >
                    {tier.price ? (
                      <>
                        <Typography component="h3" variant="h2">
                          {isAnnual ? tier.annualPrice : tier.price}
                          {t("pricing.currencySymbol")}
                        </Typography>
                        {tier.price !== "" ? (
                          <Typography component="h3" variant="h6">
                            &nbsp; {isAnnual ? t("pricing.perYear") : t("pricing.perMonth")}
                          </Typography>
                        ) : null}
                      </>
                    ) : (
                      <Typography component="h3" variant="h6" sx={{ color: "text.secondary" }}>
                        {t("pricing.noPriceAvailable")} {/* Par exemple : "Contactez-nous" */}
                      </Typography>
                    )}
                  </Box>
                  <Divider sx={{ my: 2, opacity: 0.8, borderColor: "divider" }} />
                  {Array.isArray(tier.description) &&
                    tier.description.map((line) => (
                    <Box key={line} sx={{ py: 1, display: "flex", gap: 1.5, alignItems: "center" }}>
                      <CheckCircleRoundedIcon
                        sx={{
                          width: 20,
                          color: tier.highlighted ? "primary.light" : "primary.main",
                        }}
                      />
                      <Typography
                        variant="subtitle2"
                        component={"span"}
                        sx={[
                          tier.highlighted
                            ? { color: "grey.50" }
                            : { color: null },
                        ]}
                      >
                        {line}
                      </Typography>
                    </Box>
                  ))}
                  {Array.isArray(tier.excludedFeature) &&
                  tier.excludedFeature.map((line) => (
                    <Box key={line} sx={{ py: 1, display: "flex", gap: 1.5, alignItems: "center" }}>
                      <CancelRoundedIcon
                        sx={{
                          width: 20,
                          color: tier.highlighted ? "error.light" : "error.main",
                        }}
                      />
                      <Typography
                        variant="subtitle2"
                        component={"span"}
                        sx={[
                          tier.highlighted
                            ? { color: "grey.50" }
                            : { color: null },
                        ]}
                      >
                        {line}
                      </Typography>
                    </Box>
                  ))}
                </CardContent>

                {/* Card Actions */}
                <CardActions>
                  <Button 
                    fullWidth 
                    variant={tier.buttonVariant} 
                    color={tier.buttonColor}
                    onClick={() => handlePlanClick(tier.title)}
                  >
                    {tier.buttonText}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Dialog pour l'email */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>
          {t("pricing.dialog.title", { plan: selectedPlan })}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t("pricing.dialog.emailLabel")}
            type="email"
            fullWidth
            variant="outlined"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setEmailError("");
            }}
            error={!!emailError}
            helperText={emailError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenDialog(false);
            setEmail("");
            setEmailError("");
          }} color="primary">
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSubscribe} color="primary" variant="contained">
            {t("pricing.dialog.submit")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar pour les messages */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </section>
  );
}